import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Modal from 'components/ui/Modal';
import { CancelButton, ConfirmButton } from 'components/ui/Button';
import { historyShape } from 'constants/propTypes';
import { TRAVEL_PASS_REFUNDED } from 'constants/booking';
import { getClubEurostarSubscriptionURL, getRefundMethodsURL } from 'utils/urls';
import { useManagedMutation } from 'utils/managedHooks';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { StyledCancelBeforePaymentButtons, StyledNoRevert, StyledRow } from '../SubscriptionDetails.styles';
import { ADD_NOTE_TO_BOOKING, REFUND_TRAVEL_PASS_VAT } from 'store/mutations';
import { CurrencyInput } from 'components/ui/Inputs';
import { subtract } from 'utils/calculator';
import { useForm } from 'react-hook-form';

import { Column } from 'components/shared/Layout';

const RefundVAT = ({ onClose, bookingReference, currency, membershipNumber, history, travelPassPrice, itemRef }) => {
  const { register, handleSubmit, setValue } = useForm({
    defaultValues: {
      vatRefundAmount: 1
    }
  });
  const [addNote, { loading: addNoteLoading }] = useManagedMutation(ADD_NOTE_TO_BOOKING);
  const [refundVAT, { loading: refundVATLoading, error: refundVATError }] = useManagedMutation(REFUND_TRAVEL_PASS_VAT, {
    onCompleted: () => {
      onClose();
      history.push(getRefundMethodsURL(bookingReference, currency, membershipNumber, false, true));
    }
  });

  const [feeAmount, changeFeeAmount] = useState(1);

  const handleOnSubmit = async () => {
    await refundVAT({
      variables: {
        bookingReference,
        itemRef,
        feeAmount: Number(feeAmount)
      }
    });

    await addNote({
      variables: {
        note: TRAVEL_PASS_REFUNDED,
        bookingReference
      }
    });
  };

  if (refundVATError) {
    throw new AgentPortalError(refundVATError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: getClubEurostarSubscriptionURL(membershipNumber, bookingReference)
    });
  }

  return (
    <Modal isOpen={true} title={<FormattedMessage id="subscriptions.vatRefund" />} onClose={onClose}>
      <FormattedMessage id="review.removeModal.subtitle" />

      <form
        onSubmit={handleSubmit(handleOnSubmit)}
        data-cy="subscriptions-vatRefundAmount-form"
        data-testid="subscriptions-vatRefundAmount-form"
      >
        <StyledRow columns={12}>
          <Column span={12}>
            <FormattedMessage id="subscriptions.vatRefundAmount" />:{' '}
            <CurrencyInput
              currency={currency}
              dataCy="vat-refund-amount"
              dataTestId="vat-refund-amount"
              value={feeAmount}
              min={1}
              max={subtract(travelPassPrice, 1)}
              isControlled={false}
              {...register('vatRefundAmount', {
                required: true,
                onChange: (event) => {
                  const newValue = event.target.value;

                  setValue('vatRefundAmount', newValue);
                  changeFeeAmount(newValue);
                }
              })}
              step={0.01}
            />
          </Column>
        </StyledRow>

        <StyledRow columns={12}>
          <Column span={12}>
            <FormattedMessage id="subscriptions.priceExcludingvat" />={' '}
            <span data-testid="priceExcludingvat">{subtract(travelPassPrice, feeAmount)}</span>
          </Column>
        </StyledRow>

        <StyledRow columns={12}>
          <Column span={12}>
            <StyledNoRevert>
              <FormattedMessage id="subscriptions.refundDefinitive" />
            </StyledNoRevert>
          </Column>
        </StyledRow>

        <StyledCancelBeforePaymentButtons>
          <ConfirmButton
            type="submit"
            data-testid="submit"
            data-cy="submit"
            disabled={refundVATLoading || addNoteLoading}
            loading={refundVATLoading || addNoteLoading}
            variant="primary"
          >
            <FormattedMessage id="manageBooking.confirmCancellation" />
          </ConfirmButton>
          <CancelButton onClick={onClose} data-testid="manageBooking.abandonCancellation" disabled={refundVATLoading || addNoteLoading}>
            <FormattedMessage id="manageBooking.abandonCancellation" />
          </CancelButton>
        </StyledCancelBeforePaymentButtons>
      </form>
    </Modal>
  );
};

RefundVAT.propTypes = {
  onClose: PropTypes.func,
  bookingReference: PropTypes.string,
  currency: PropTypes.string,
  membershipNumber: PropTypes.string,
  history: historyShape,
  travelPassPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  itemRef: PropTypes.string
};

export default RefundVAT;
